import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { StoreBadge } from "@/components/AppBadges";
import { LeFil } from "@/components/LeFil";
import { PhoneFrame, DashboardScreen } from "@/components/PhoneMockup";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  return (
    <section className="grain mesh-light relative overflow-hidden border-b border-border bg-sand dark:bg-transparent">
      <div className="mx-auto max-w-7xl px-5 pb-16 pt-14 sm:px-8 lg:pb-24 lg:pt-20">
        <div className="mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-4 py-1.5 text-xs font-medium text-foreground/70 backdrop-blur"
          >
            <ShieldCheck className="h-4 w-4 text-marigold-deep" />
            Le fil qui relie votre famille
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.05 }}
            className="mt-6 font-heading text-5xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-6xl lg:text-7xl"
          >
            Care for the{" "}
            <span className="italic font-medium text-rose-deep">ones you love</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.12 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl"
          >
            One beautifully simple app to keep your whole family connected and
            protected — medication reminders, Emergency SOS, an AI Family
            Coordinator, medical passport and more.
          </motion.p>

          <motion.div
            id="download"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.2 }}
            className="mt-8 flex scroll-mt-24 flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <Link
              to="/features"
              data-testid="hero-primary-cta"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-7 py-4 text-sm font-semibold text-sand transition-all duration-200 hover:opacity-90 active:scale-[0.98] sm:w-auto dark:bg-primary dark:text-primary-foreground"
            >
              Discover the app <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-secondary-cta"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-foreground/30 px-7 py-4 text-sm font-semibold text-foreground transition-all duration-200 hover:bg-foreground/5 active:scale-[0.98] sm:w-auto"
            >
              Talk to us
            </Link>
          </motion.div>
        </div>

        {/* Le Fil — signature connecting arc */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.28 }}
          className="mx-auto mt-10 max-w-2xl text-foreground/80"
        >
          <LeFil from="Boston" to="Lisbon" className="h-auto w-full" />
        </motion.div>

        {/* App preview */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.35 }}
          className="relative mt-6 flex flex-col items-center"
        >
          <div className="absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-marigold/20 blur-3xl" />
          <div className="animate-float">
            <PhoneFrame>
              <DashboardScreen />
            </PhoneFrame>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <StoreBadge store="apple" />
            <StoreBadge store="google" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
